import React from 'react';
import { SearchIcon, MicIcon, ArrowUpIcon, PlusIcon, SunIcon, ChevronRightIcon, UserRoundIcon, LeafIcon, BusIcon, BookOpenIcon, FileTextIcon, MailIcon, BellIcon, SmartphoneIcon, PinIcon, CreditCardIcon, WalletIcon, CalendarIcon, IdCardIcon, SettingsIcon, TrashIcon, PhoneIcon, MapIcon, HomeIcon, GridIcon, TriangleAlertIcon } from 'lucide-react';
import { mockupCopy, type Bilingual } from '../../data/platformContent';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/063c2963-c0db-45da-b2ee-49597e11c430.jpg';
type Props = {
  isFR: boolean;
};
const use = (isFR: boolean) => (value: Bilingual) => isFR ? value.fr : value.en;
const SHORTCUT_ICONS = [UserRoundIcon, LeafIcon, LeafIcon, BusIcon, BookOpenIcon, FileTextIcon];
const PORTAL_ICONS = [FileTextIcon, CreditCardIcon, CalendarIcon, FileTextIcon, IdCardIcon, BellIcon, SettingsIcon];
const APP_TILE_ICONS = [MapIcon, FileTextIcon, WalletIcon, CalendarIcon, TrashIcon, PhoneIcon];
const APP_TAB_ICONS = [HomeIcon, GridIcon, IdCardIcon, UserRoundIcon];

/** 01 — a full civic website with ATNA embedded in the experience. */
export function WebsiteMockup({
  isFR
}: Props) {
  const t = use(isFR);
  return <div className="rounded-xl border border-[#E4E6EC] bg-white overflow-hidden shadow-[0_8px_28px_rgba(18,18,18,0.10)]" data-id="element-5275" data-mp="3pjehn0rn6ma5">
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 h-5 px-2.5 bg-[#F4F5F9] border-b border-[#E4E6EC]" data-id="element-5276" data-mp="h4mkrese75mf">
        <span className="w-1.5 h-1.5 rounded-full bg-[#D6D9E2]" data-id="element-5277" data-mp="m24j6b8sa2g6" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#D6D9E2]" data-id="element-5278" data-mp="7d024xdq649n" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#D6D9E2]" data-id="element-5279" data-mp="cd4h2iyziqxr" />
        <span className="ml-3 flex-1 h-2 rounded-full bg-white border border-[#E4E6EC]" data-id="element-5280" data-mp="cb9znemx9gyf" />
      </div>

      {/* Site navigation */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-[#E4E6EC]" data-id="element-5281" data-mp="14tfc0xy2kpap">
        <span className="w-5 h-5 rounded bg-[#4F46E5]/10 shrink-0" aria-hidden="true" data-id="element-5282" data-mp="14v8j2eclhbj8" />
        <div className="leading-none" data-id="element-5283" data-mp="1enbd0tng4qdg">
          <div className="font-sans text-[9px] font-semibold text-[#121212]" data-id="element-5284" data-mp="19pt4339fib5g">
            {t(mockupCopy.cityName)}
          </div>
          <div className="font-sans text-[7px] text-[#9AA0B0]" data-id="element-5285" data-mp="14pwveqpsdvjk">
            {t(mockupCopy.cityTagline)}
          </div>
        </div>
        <ul className="hidden sm:flex items-center gap-3 ml-2" data-id="element-5286" data-mp="10rz85tfktjit">
          {mockupCopy.nav.map(item => <li key={item.en} className="font-sans text-[8px] text-[#5B6478]" data-id="element-5287" data-mp="qzthqo4z3a5q">
              {t(item)}
            </li>)}
        </ul>
        <div className="ml-auto flex items-center gap-2 text-[#9AA0B0]" data-id="element-5288" data-mp="m1rxhzvt94so">
          <SearchIcon className="w-2.5 h-2.5" strokeWidth={2} data-id="element-5289" data-mp="h25m7vzjlymi" />
          <span className="font-sans text-[7px]" data-id="element-5290" data-mp="gzkit2jljamn">FR | EN</span>
          <MailIcon className="w-2.5 h-2.5" strokeWidth={2} data-id="element-5291" data-mp="cz0n83eo8f2m" />
          <span className="relative" data-id="element-5292" data-mp="817gn99ghj1t">
            <BellIcon className="w-2.5 h-2.5" strokeWidth={2} data-id="element-5293" data-mp="82a6ddv1lfg5" />
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#E11D48]" data-id="element-5294" data-mp="2joknc33ekawo" />
          </span>
          <SmartphoneIcon className="w-2.5 h-2.5" strokeWidth={2} data-id="element-5295" data-mp="2omm9iww4kqcd" />
        </div>
      </div>

      {/* Hero + contextual rail */}
      <div className="relative" data-id="element-5296" data-mp="2spiidvvv5834">
        <img src={HERO_IMAGE} alt="" aria-hidden="true" className="w-full h-[150px] sm:h-[178px] object-cover" data-id="element-5297" data-mp="2sn617kxxj1r4" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1020]/70 via-[#0B1020]/25 to-transparent" aria-hidden="true" data-id="element-5298" data-mp="20ze3udusbf9t" />

        <div className="absolute inset-0 flex" data-id="element-5299" data-mp="29xzihxx4ya5b">
          <div className="flex-1 min-w-0 p-3 flex flex-col justify-end" data-id="element-5300" data-mp="29x6jjj4f4oxq">
            <p className="font-serif text-[15px] sm:text-[17px] text-white leading-tight drop-shadow" data-id="element-5301" data-mp="2eq9dvth0s2ez">
              {t(mockupCopy.webHeadline)}
            </p>

            {/* Natural-language action box */}
            <div className="mt-2.5 rounded-lg bg-white shadow-[0_4px_14px_rgba(18,18,18,0.18)] px-2 py-1.5" data-id="element-5302" data-mp="1m7lxusgvrdkr">
              <div className="flex items-center gap-1.5" data-id="element-5303" data-mp="3ow3ufz4nwb71">
                <PlusIcon className="w-2.5 h-2.5 text-[#9AA0B0]" strokeWidth={2} data-id="element-5304" data-mp="3jx0u2rzmmt5e" />
                <span className="flex-1 font-sans text-[8px] text-[#9AA0B0]" data-id="element-5305" data-mp="1i9jkup3ovxy">
                  {t(mockupCopy.askPlaceholder)}
                </span>
                <MicIcon className="w-2.5 h-2.5 text-[#9AA0B0]" strokeWidth={2} data-id="element-5306" data-mp="3svmd0o90ga6p" />
                <span className="w-4 h-4 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-5307" data-mp="bfmnp0txemwt">
                  <ArrowUpIcon className="w-2 h-2 text-white" strokeWidth={2.5} data-id="element-5308" data-mp="6gt6axbiqbrd" />
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-5309" data-mp="kgkq5eopumda">
                {mockupCopy.askSuggestions.map(s => <span key={s.en} className="rounded-full border border-[#E4E6EC] px-1.5 py-0.5 font-sans text-[7px] text-[#5B6478]" data-id="element-5310" data-mp="gdxwr9cectdz">
                    {t(s)}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Recommendations rail */}
          <div className="hidden sm:flex w-[34%] shrink-0 flex-col gap-1 p-2 justify-end" data-id="element-5311" data-mp="2c3ihsc4i2wls">
            {mockupCopy.events.map(event => <div key={event.title.en} className="flex items-center gap-1.5 rounded-md bg-white/95 px-1.5 py-1 shadow-[0_2px_8px_rgba(18,18,18,0.12)]" data-id="element-5312" data-mp="1ybkqlgaqvldw">
                <img src={event.image} alt="" aria-hidden="true" className="w-6 h-6 rounded object-cover shrink-0" data-id="element-5313" data-mp="1ohkcwq1ed7tk" />
                <div className="min-w-0" data-id="element-5314" data-mp="1kdmekhjwc11k">
                  <div className="font-sans text-[8px] font-medium text-[#121212] truncate" data-id="element-5315" data-mp="1kenoujt38ji8">
                    {t(event.title)}
                  </div>
                  <div className="font-sans text-[7px] text-[#9AA0B0] truncate" data-id="element-5316" data-mp="3erncn07mgk0l">
                    {t(event.when)}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Contextual weather strip */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E4E6EC]" data-id="element-5317" data-mp="38a3qc5e3bhky">
        <SunIcon className="w-3.5 h-3.5 text-[#F59E0B]" strokeWidth={2} data-id="element-5318" data-mp="33cu38we6bi0a" />
        <div className="min-w-0" data-id="element-5319" data-mp="2ye0qx7amhx1p">
          <div className="font-sans text-[8px] font-medium text-[#121212]" data-id="element-5320" data-mp="2yf2179jtefid">
            {t(mockupCopy.weather.value)}
          </div>
          <div className="font-sans text-[7px] text-[#9AA0B0] truncate" data-id="element-5321" data-mp="3r1wgls1ryooz">
            {t(mockupCopy.weather.caption)}
          </div>
        </div>
        <ChevronRightIcon className="w-3 h-3 text-[#C8CCD8] ml-auto" strokeWidth={2} data-id="element-5322" data-mp="3m9v90jfjxm5a" />
      </div>

      {/* Service navigation */}
      <div className="grid grid-cols-6 px-2 py-2" data-id="element-5323" data-mp="iltc1v5ylg4v">
        {mockupCopy.webShortcuts.map((shortcut, index) => {
        const Icon = SHORTCUT_ICONS[index];
        const active = index === 0;
        return <div key={shortcut.en} className="text-center px-0.5" data-id="element-5324" data-mp="l7atiudtlspq" data-mpi={index}>
              <Icon className={`w-3 h-3 mx-auto ${active ? 'text-[#E11D48]' : 'text-[#5B6478]'}`} strokeWidth={1.75} aria-hidden="true" data-id="element-5325" data-mp="q5m3r681258h" />
              <div className="font-sans text-[7px] text-[#5B6478] mt-1 leading-tight" data-id="element-5326" data-mp="103s2jeyjev94">
                {t(shortcut)}
              </div>
            </div>;
      })}
      </div>
    </div>;
}

/** 02 — the authenticated citizen portal. */
export function PortalMockup({
  isFR
}: Props) {
  const t = use(isFR);
  const {
    portal
  } = mockupCopy;
  return <div className="rounded-xl border border-[#E4E6EC] bg-white overflow-hidden shadow-[0_8px_28px_rgba(18,18,18,0.10)]" data-id="element-5327" data-mp="1dvoqr7x34l08">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E4E6EC]" data-id="element-5328" data-mp="1iwcki1cp7fy5">
        <span className="w-6 h-6 rounded bg-[#4F46E5]/10 shrink-0" aria-hidden="true" data-id="element-5329" data-mp="1nv78ritfpwpn" />
        <div className="leading-none" data-id="element-5330" data-mp="1rvhdyt9ekj3g">
          <div className="font-sans text-[9px] font-semibold text-[#121212]" data-id="element-5331" data-mp="2d5we36iiah43">
            {t(mockupCopy.cityName)}
          </div>
          <div className="font-sans text-[7px] text-[#9AA0B0]" data-id="element-5332" data-mp="2952qyfcw16c9">
            {t(portal.title)}
          </div>
        </div>
        <PinIcon className="w-3 h-3 text-[#C8CCD8] ml-auto" strokeWidth={2} data-id="element-5333" data-mp="2n3kb633l40pf" />
      </div>

      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#E4E6EC]" data-id="element-5334" data-mp="2i6ap8xfem76c">
        <span className="w-7 h-7 rounded-full bg-[#4F46E5] text-white font-sans text-[8px] flex items-center justify-center shrink-0" data-id="element-5335" data-mp="1uen2mrp6di9v">
          MT
        </span>
        <div className="leading-tight" data-id="element-5336" data-mp="2457zoap7aeqo">
          <div className="font-sans text-[9px] font-medium text-[#121212]" data-id="element-5337" data-mp="2469n7enektpa">
            {t(portal.personName)}
          </div>
          <div className="font-sans text-[7px] text-[#9AA0B0]" data-id="element-5338" data-mp="1zd6rp0z2fa6g">
            {t(portal.personRole)}
          </div>
        </div>
      </div>

      <ul data-id="element-5339" data-mp="16q3vifhv3bjd">
        {portal.items.map((item, index) => {
        const Icon = PORTAL_ICONS[index];
        return <li key={item.label.en} className="flex items-center gap-2 px-3 py-[0.6rem] border-b border-[#F0F1F5] last:border-b-0" data-id="element-5340" data-mp="3icxrackksf9p" data-mpi={index}>
              <Icon className="w-3 h-3 text-[#4F46E5] shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-5341" data-mp="39crdepkytda0" />
              <span className="font-sans text-[9px] text-[#121212]/80" data-id="element-5342" data-mp="3dc07jvjx3d63">
                {t(item.label)}
              </span>
              {item.badge && <span className="ml-auto w-3.5 h-3.5 rounded-full bg-[#E11D48] text-white font-sans text-[7px] flex items-center justify-center" data-id="element-5343" data-mp="9xm9r24ajw49">
                  {item.badge}
                </span>}
            </li>;
      })}
      </ul>
    </div>;
}

/** 03 — the mobile app. */
export function AppMockup({
  isFR
}: Props) {
  const t = use(isFR);
  const {
    app
  } = mockupCopy;
  return <div className="mx-auto w-[196px] rounded-[1.6rem] border-[5px] border-[#121212] bg-[#121212] overflow-hidden shadow-[0_10px_30px_rgba(18,18,18,0.22)]" data-id="element-5344" data-mp="2lnonyvk3ag4y">
      <div className="rounded-[1.15rem] bg-white overflow-hidden" data-id="element-5345" data-mp="2882wh1vo1aw7">
        {/* Status bar */}
        <div className="flex items-center justify-between px-2.5 pt-1.5 pb-1" data-id="element-5346" data-mp="1ugxrdk9j3qeh">
          <span className="font-sans text-[7px] text-[#121212]" data-id="element-5347" data-mp="1uetn317pwpdo">9:41</span>
          <span className="w-10 h-2 rounded-full bg-[#121212]" aria-hidden="true" data-id="element-5348" data-mp="1zeg4znyhqtst" />
          <span className="font-sans text-[7px] text-[#121212]" data-id="element-5349" data-mp="1khp4tcgzte25">▮▮</span>
        </div>

        {/* Header over photo */}
        <div className="relative" data-id="element-5350" data-mp="1pj4nixvp78hd">
          <img src={HERO_IMAGE} alt="" aria-hidden="true" className="w-full h-14 object-cover" data-id="element-5351" data-mp="1bi21gl4pdy6g" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1020]/70 to-[#0B1020]/10" aria-hidden="true" data-id="element-5352" data-mp="1ghomhgqrwipt" />
          <div className="absolute inset-0 flex items-start justify-between p-2" data-id="element-5353" data-mp="3auw9wncf5s1f">
            <div className="leading-tight" data-id="element-5354" data-mp="3astko5j3c2xr">
              <div className="font-sans text-[9px] font-semibold text-white" data-id="element-5355" data-mp="3l05ygn8f48q0">
                {t(mockupCopy.cityName)}
              </div>
              <div className="font-sans text-[7px] text-white/80" data-id="element-5356" data-mp="3fxx5vrzgj8of">
                {t(mockupCopy.cityTaglineMobile)}
              </div>
            </div>
            <span className="relative" data-id="element-5357" data-mp="3ozn71pj55lze">
              <BellIcon className="w-3 h-3 text-white" strokeWidth={2} data-id="element-5358" data-mp="3oylhyh5aiyyo" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#E11D48] text-white text-[5px] flex items-center justify-center" data-id="element-5359" data-mp="7iwvcbw9giog">
                1
              </span>
            </span>
          </div>
        </div>

        {/* Service tiles */}
        <div className="grid grid-cols-3 gap-1.5 p-2" data-id="element-5360" data-mp="cirq4qj5pses">
          {app.tiles.map((tile, index) => {
          const Icon = APP_TILE_ICONS[index];
          return <div key={tile.en} className="rounded-lg border border-[#E4E6EC] bg-white py-2 px-1 text-center" data-id="element-5361" data-mp="2ie2pve3f2oy3" data-mpi={index}>
                <Icon className="w-3 h-3 text-[#4F46E5] mx-auto" strokeWidth={1.75} aria-hidden="true" data-id="element-5362" data-mp="hbbl7p0hasf9" />
                <div className="font-sans text-[6.5px] text-[#5B6478] mt-1 leading-tight" data-id="element-5363" data-mp="r7y6gewpvlht">
                  {t(tile)}
                </div>
              </div>;
        })}
        </div>

        {/* Real-time notice */}
        <div className="mx-2 mb-2 rounded-lg border border-[#FBD5D5] bg-[#FEF2F2] px-2 py-1.5 flex items-start gap-1.5" data-id="element-5364" data-mp="108csxhlf36mw">
          <TriangleAlertIcon className="w-2.5 h-2.5 text-[#E11D48] shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" data-id="element-5365" data-mp="155wb2sl8k3k4" />
          <div className="leading-tight min-w-0" data-id="element-5366" data-mp="1ewh6k75m4ryt">
            <div className="font-sans text-[7px] font-semibold text-[#121212]" data-id="element-5367" data-mp="1jwvb3f809js3">
              {t(app.alertTitle)}
            </div>
            <div className="font-sans text-[6.5px] text-[#5B6478]" data-id="element-5368" data-mp="1nzib43etyb0d">
              {t(app.alertBody)}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="grid grid-cols-4 border-t border-[#E4E6EC] py-1.5" data-id="element-5369" data-mp="34f5u97ti39ts">
          {app.tabs.map((tab, index) => {
          const Icon = APP_TAB_ICONS[index];
          const active = index === 0;
          return <div key={tab.en} className="text-center" data-id="element-5370" data-mp="2uhrr29wl8hjf" data-mpi={index}>
                <Icon className={`w-3 h-3 mx-auto ${active ? 'text-[#4F46E5]' : 'text-[#C8CCD8]'}`} strokeWidth={1.75} aria-hidden="true" data-id="element-5371" data-mp="3s4idl0ijky9t" />
                <div className={`font-sans text-[6px] mt-0.5 ${active ? 'text-[#4F46E5]' : 'text-[#9AA0B0]'}`} data-id="element-5372" data-mp="3iefeciktuo31">
                  {t(tab)}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}